import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';
import { PageTransition } from '@/components/motion';
import { brokersAPI } from '@/services/apolloApi';
import { useApollo } from '@/context/ApolloContext';
import { WorkflowNode, NODE_TYPES_CONFIG } from '@/components/Apollo/WorkflowNode';

const nodeTypes = { workflowNode: WorkflowNode };

function WorkflowEditorInner() {
  const { brokerId } = useParams();
  const navigate = useNavigate();
  const reactFlowInstance = useReactFlow();
  const { setWorkflowMode, selectedNode, setSelectedNode, setDrawerOpen } = useApollo();
  const [broker, setBroker] = useState(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [saving, setSaving] = useState(false);
  const reactFlowWrapper = useRef(null);

  // Enter/exit workflow mode for the drawer
  useEffect(() => {
    setWorkflowMode(true);
    setDrawerOpen(true);
    return () => {
      setWorkflowMode(false);
      setSelectedNode(null);
    };
  }, []);

  // Sync selected node config changes from drawer back to React Flow nodes
  useEffect(() => {
    if (!selectedNode) return;
    setNodes((nds) =>
      nds.map((n) =>
        n.id === selectedNode.id
          ? { ...n, data: { ...n.data, config: selectedNode.data.config } }
          : n
      )
    );
  }, [selectedNode?.data?.config]);

  useEffect(() => {
    if (!brokerId) return;
    brokersAPI.getById(brokerId).then((b) => {
      setBroker(b);
      if (b.workflow?.nodes?.length > 0) {
        setNodes(b.workflow.nodes);
        setEdges(b.workflow.edges || []);
      }
    }).catch(console.error);
  }, [brokerId]);

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge({ ...params, animated: true, style: { stroke: 'hsl(var(--primary))' } }, eds)),
    [setEdges]
  );

  const onNodeClick = useCallback((_, node) => {
    setSelectedNode(node);
  }, [setSelectedNode]);

  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
  }, [setSelectedNode]);

  // Handle drop from the drawer palette
  const onDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    const nodeType = e.dataTransfer.getData('application/reactflow-nodetype');
    if (!nodeType) return;

    const cfg = NODE_TYPES_CONFIG[nodeType];
    if (!cfg) return;

    const position = reactFlowInstance.screenToFlowPosition({
      x: e.clientX,
      y: e.clientY,
    });

    const id = `${nodeType}_${Date.now()}`;
    const newNode = {
      id,
      type: 'workflowNode',
      position,
      data: {
        label: cfg.label,
        nodeType,
        icon: cfg.icon,
        color: cfg.color,
        config: { ...cfg.defaultConfig },
      },
    };
    setNodes((nds) => [...nds, newNode]);
    setSelectedNode(newNode);
  }, [reactFlowInstance, setNodes, setSelectedNode]);

  async function handleSave() {
    if (!brokerId) return;
    setSaving(true);
    try {
      await brokersAPI.saveWorkflow(brokerId, { nodes, edges });
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <PageTransition className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-white/10 bg-card/60">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(`/apollo/brokers/${brokerId}`)}
            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent/10 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <span className="text-sm font-semibold text-foreground">
            {broker?.name || 'Workflow'} — Editor
          </span>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md bg-primary text-primary-foreground hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Save
        </button>
      </div>

      {/* Canvas */}
      <div className="flex-1 relative" ref={reactFlowWrapper}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={onNodeClick}
          onPaneClick={onPaneClick}
          onDragOver={onDragOver}
          onDrop={onDrop}
          nodeTypes={nodeTypes}
          fitView
          className="bg-background"
          defaultEdgeOptions={{ animated: true, style: { stroke: 'hsl(var(--primary))', strokeWidth: 2 } }}
        >
          <Background color="hsl(240 4% 16%)" gap={20} size={1} />
          <Controls className="!bg-card !border-white/10 !rounded-lg" />
          <MiniMap
            className="!bg-card !border-white/10 !rounded-lg"
            nodeColor="hsl(var(--primary))"
            maskColor="rgba(0,0,0,0.7)"
          />
        </ReactFlow>
      </div>
    </PageTransition>
  );
}


export default function WorkflowEditor() {
  return (
    <ReactFlowProvider>
      <WorkflowEditorInner />
    </ReactFlowProvider>
  );
}
